/** Lightweight in-memory stale-while-revalidate cache for Tenant
 * Backoffice page data
 * (VISTOERA_CANVAS_TO_TENANT_BACKOFFICE_RETURN_STATE_RESTORE_SKELETON_FLASH_ELIMINATION_AND_LAST_BACKOFFICE_LOCATION_MEMORY_P0_V1
 * §44-§57).
 *
 * Forensic root cause (SKELETON_FLASH_ROOT_CAUSE — §45/§182): this
 * codebase has no query-cache library (`QUERY_CACHE_LIBRARY=none` —
 * every Tenant Backoffice page fetches directly via `saas/api.ts`
 * (axios) into local `useState`, see `WorkspaceOverviewPage`,
 * `AudioAssetsPage`, `TextAssetsPage`). `WorkspaceOverviewPage` is a
 * normal nested-route child of `WorkspaceLayout`'s `<Outlet/>` — it
 * fully UNMOUNTS while Canvas is open (Canvas is `onProductionCanvas`'s
 * OWN early-return branch, same component tree, different `<Outlet/>`
 * child) and fully REMOUNTS fresh the moment the user returns, always
 * starting from `useState(true)`/`useState(null)` and refetching from
 * zero — even though the exact same data was on screen seconds earlier.
 * `WorkspaceOverviewPage`'s `if (loading && !overview) return
 * <Skeleton/><Skeleton/>` branch (2 blocks, rows 2 + rows 4 = 6 bars)
 * is the literal component instance that paints the reported
 * screenshot's horizontal Skeleton bars — reproduced live against
 * Preview during this task's forensic pass.
 *
 * Fix model (spec §52/§74): render cached data instantly on remount,
 * then silently revalidate in the background — a minimal generic
 * module rather than adopting a full query-cache library wholesale
 * (spec §47: this is not a blanket rewrite of every Backoffice page's
 * data fetching, only the ones that actually exhibited the bug). Cache
 * is intentionally memory-only (module-scope `Map`, cleared on hard
 * reload/new tab) — this is a UX smoothing layer, never a source of
 * truth, and per spec §69/§71-73 must never become a substitute for
 * real revalidation (no `staleTime: Infinity`, no disabled revalidation
 * — every mount with cached data ALSO kicks off a fresh fetch). */
import { useEffect, useRef, useState } from 'react'

interface CacheEntry<T> {
  data: T
  updatedAt: number
}

const cache = new Map<string, CacheEntry<unknown>>()

export function getCachedBackofficeResource<T>(key: string): T | undefined {
  return (cache.get(key) as CacheEntry<T> | undefined)?.data
}

export function setCachedBackofficeResource<T>(key: string, data: T): void {
  cache.set(key, { data, updatedAt: Date.now() })
}

/** Test-only escape hatch — clears every cached resource so tests don't
 * bleed state across cases (mirrors `clearTenantBackofficeReturnLocation`'s
 * role for the return-location store). */
export function clearAllCachedBackofficeResources(): void {
  cache.clear()
}

export interface StaleWhileRevalidateResult<T> {
  /** Best-known data — cached (possibly stale) or freshly fetched. */
  data: T | undefined
  /** True ONLY when there is no data at all yet (a genuine first-ever
   * load, no cache) — the only case allowed to show a blocking
   * loading UI (spec §54 `isInitialLoading` vs `isFetching`). */
  isInitialLoading: boolean
  /** True while a fetch is in flight AND cached data already exists —
   * callers may show a small non-blocking indicator (spec §56), but
   * must never replace the page with a full loading surface (spec
   * §53/§55). */
  isRevalidating: boolean
  /** Only set when `data` is still `undefined` (an initial load that
   * has nothing to fall back on failed — spec §154). A background
   * revalidation failure with existing cached `data` never sets this
   * (spec §151/§152 — cached content stays, no flash-to-error). */
  error: Error | null
  reload: () => void
}

/**
 * Generic stale-while-revalidate resource hook (spec §52/§74 "Render
 * immediately + Revalidate silently"). `key` is `null` to skip fetching
 * entirely (e.g. `workspaceId` not resolved yet). The fetcher re-runs
 * whenever `key` changes and once more per explicit `reload()` call —
 * never on a timer/poll (spec §170 "不增加 background polling").
 */
export function useStaleWhileRevalidate<T>(key: string | null, fetcher: () => Promise<T>): StaleWhileRevalidateResult<T> {
  const initialCached = key ? getCachedBackofficeResource<T>(key) : undefined
  const [data, setData] = useState<T | undefined>(initialCached)
  const [isInitialLoading, setIsInitialLoading] = useState<boolean>(Boolean(key) && initialCached === undefined)
  const [isRevalidating, setIsRevalidating] = useState<boolean>(Boolean(key) && initialCached !== undefined)
  const [error, setError] = useState<Error | null>(null)
  const [reloadToken, setReloadToken] = useState(0)
  const fetcherRef = useRef(fetcher)
  fetcherRef.current = fetcher

  useEffect(() => {
    if (!key) return
    const existing = getCachedBackofficeResource<T>(key)
    let cancelled = false
    if (existing !== undefined) {
      setData(existing)
      setIsInitialLoading(false)
      setIsRevalidating(true)
    } else {
      setData(undefined)
      setIsInitialLoading(true)
      setIsRevalidating(false)
    }
    setError(null)
    fetcherRef
      .current()
      .then((result) => {
        if (cancelled) return
        setCachedBackofficeResource(key, result)
        setData(result)
        setError(null)
      })
      .catch((err: unknown) => {
        if (cancelled) return
        if (existing === undefined) {
          // No cached data to fall back on — a genuine initial-load
          // failure (spec §154), surface it.
          setError(err instanceof Error ? err : new Error(String(err)))
        }
        // else: a stale-revalidation failure (spec §151) — keep
        // showing the already-cached `data`; deliberately swallowed
        // here rather than clearing the page to a generic failure.
      })
      .finally(() => {
        if (cancelled) return
        setIsInitialLoading(false)
        setIsRevalidating(false)
      })
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, reloadToken])

  return { data, isInitialLoading, isRevalidating, error, reload: () => setReloadToken((t) => t + 1) }
}
